import axios from 'axios';
import { useAuthStore } from '../store/authStore';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const getToken = () => useAuthStore.getState().token;

const authHeaders = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

// ==================== Types ====================

export interface ContinueLearning {
  courseId: string;
  courseTitle: string;
  chapterId: string | null;
  topicId: string;
  topicTitle: string | null;
  progress: number;
  lastAccessAt: string | null;
}

export interface StudentCourseCard {
  _id: string;
  courseCode: string;
  title: string;
  description: string;
  teacherName: string | null;
  totalTopics: number;
  progress: number;
  lastAccessAt: string | null;
}

/**
 * Deliberately contains no cohort comparison, rank, or percentile.
 * Student-facing progress is strictly self-referential.
 */
export interface StudentDashboardData {
  continueLearning: ContinueLearning | null;
  upcomingDeadlines: Array<{
    _id: string;
    title: string;
    type: string;
    dueAt: string;
    isHard: boolean;
    courseId: string;
    courseTitle: string | null;
    daysUntil: number;
  }>;
  courses: StudentCourseCard[];
  stats: {
    activitiesCompleted: number;
    readingCompleted: number;
    exercisesCompleted: number;
    selfExplanationsCompleted: number;
    studyTimeMinutes: number;
    streakDays: number;
    averageProgress: number;
  };
  recentActivity: Array<{
    activityType: string;
    topicTitle: string | null;
    completedAt: string;
  }>;
}

export interface AtRiskStudent {
  studentId: string;
  studentName: string | null;
  courseId: string;
  progress: number;
  cohortMedian: number;
  progressGap: number;
  inactiveDays: number | null;
  exerciseAttempts: number;
  exerciseCorrect: number;
  exerciseFailureRate: number | null;
  studyTimeMinutes: number;
  signals: string[];
  riskLevel: 'high' | 'medium' | 'none';
}

export interface TeacherDashboardData {
  stats: {
    courses: number;
    published: number;
    students: number;
    topics: number;
    atRiskHigh: number;
    atRiskMedium: number;
  };
  courses: Array<{
    _id: string;
    courseCode: string;
    title: string;
    isPublished: boolean;
    status: string;
    enrolled: number;
    totalTopics: number;
    cohortMedian: number;
  }>;
  atRisk: AtRiskStudent[];
  roster: AtRiskStudent[];
  criteria: {
    inactiveDays: number;
    progressGapPoints: number;
    exerciseFailureRatePercent: number;
    minAttemptsForFailureRate: number;
  };
}

export interface AdminDashboardData {
  users: {
    total: number;
    infoCompleted: number;
    completionRate: number;
    newThisWeek: number;
    byRole: Array<{ role: string; count: number }>;
  };
  platform: { courses: number; topics: number; publishedTopics: number; activities: number };
  health: { orphanedProfiles: number; unpublishedTopics: number };
  recentUsers: Array<{ name: string; primaryRole: string; infoCompleted: boolean; createdAt: string }>;
}

export interface AnalystDashboardData {
  consent: {
    totalStudents: number;
    granted: number;
    declined: number;
    notRecorded: number;
    coverageRate: number;
    researchReady: boolean;
  };
  demographics: {
    gender: Array<{ key: string; count: number }>;
    statsLevel: Array<{ key: string; count: number }>;
    college: Array<{ key: string; count: number }>;
    statsBackground: Array<{ key: string; count: number }>;
  };
  engagement: Array<{ activityType: string; total: number; completed: number }>;
  dataQuality: Record<string, string>;
}

export interface ContentManagerDashboardData {
  stats: { topics: number; published: number; drafts: number; categories: number; questions: number };
  categories: Array<{ name: string; slug: string; code: string; topicsCount: number }>;
  questionsByType: Array<{ type: string; count: number }>;
  recentlyEdited: Array<{ title: string; slug: string; published: boolean; updatedAt: string }>;
  hardestExercises: Array<{
    exerciseId: string;
    attempts: number;
    correct: number;
    failureRate: number;
    topicId: string | null;
    topicTitle: string | null;
    topicSlug: string | null;
  }>;
}

export interface SuperAdminDashboardData {
  stats: { roles: number; permissions: number; admins: number; users: number; activeToday: number };
  roles: Array<{ name: string; priority: number; permissionCount: number; isActive: boolean }>;
  recentActions: Array<{ action: string; target: string; timestamp: string; by: string | null }>;
  health: { database: string; databaseName: string; uptimeSeconds: number };
}

export interface ModeratorDashboardData {
  stats: {
    totalExplanations: number;
    withAiFeedback: number;
    missingAiFeedback: number;
    flaggedShortHighConfidence: number;
    needsReview: number;
  };
  byUnderstanding: Array<{ understanding: string; count: number; avgTokenCount: number; avgCharCount: number }>;
  flagged: Array<{ topicTitle: string | null; understanding: string; tokenCount: number; timeSpent: number; createdAt: string; reason: string }>;
  needsReview: Array<{ topicTitle: string | null; tokenCount: number; understanding: string; createdAt: string }>;
  note: string;
}

// ==================== API ====================

async function fetchDashboard<T>(path: string, signal?: AbortSignal): Promise<T> {
  const res = await axios.get(`${API_BASE_URL}/dashboard/${path}`, {...authHeaders(), signal});
  return res.data.data as T;
}

export const dashboardApi = {
  getStudent: () => fetchDashboard<StudentDashboardData>('student'),
  getTeacher: (signal?: AbortSignal) => fetchDashboard<TeacherDashboardData>('teacher', signal),
  getAdmin: () => fetchDashboard<AdminDashboardData>('admin'),
  getAnalyst: () => fetchDashboard<AnalystDashboardData>('analyst'),
  getContentManager: () => fetchDashboard<ContentManagerDashboardData>('content-manager'),
  getSuperAdmin: () => fetchDashboard<SuperAdminDashboardData>('super-admin'),
  getModerator: () => fetchDashboard<ModeratorDashboardData>('moderator')
};

export default dashboardApi;
